import {
  GLOBAL_TOOL_WHITELIST,
  COUNTRY_SPECIFIC_TOOL_WHITELISTS
} from '../lib/constants';
import { cloneDeep } from 'lodash';

export default ({
  psData = {},
  mtsData = {},
  country = '',
  isLoggedIn = false
}) => {
  let { tools = [], categories = [], sections = [] } = psData;

  const toolFilter = ({ appKey = '' }) => {
    return (
      mtsData[appKey] ||
      GLOBAL_TOOL_WHITELIST.includes(appKey) ||
      (COUNTRY_SPECIFIC_TOOL_WHITELISTS[country] &&
        COUNTRY_SPECIFIC_TOOL_WHITELISTS[country].includes(appKey))
    );
  };

  // 1st filter the tools
  tools = tools.filter(toolFilter);

  const defaultRecommended = tools.slice(0, 2);

  // ...then go back and filter out any sections that no longer have tools
  // sectionKey is defaulted to random string to filter out sections with
  // no content
  sections = sections.filter(({ sectionKey: sk = 'heisenberg' }) => {
    return tools.some(({ sectionKey = '' }) => sectionKey === sk);
  });

  // ...then go back and filter out any categories that no longer have tools
  // ditto as above for random default string for categoryKey
  categories = categories.filter(({ categoryKey: cK = 'schrodinger' }) => {
    return tools.some(({ categoryKey = '' }) => categoryKey === cK);
  });

  let args = {
    ...psData,
    tools,
    categories,
    sections,
    defaultRecommended,
    isLoggedIn
  };

  const structuredData = buildTree(args);

  tools = tools.reduce((acc, tool) => {
    acc[tool.appKey] = tool;
    return acc;
  }, {});

  sections = sections.reduce((acc, section) => {
    acc[section.sectionKey] = section;
    return acc;
  }, {});

  categories = categories.reduce((acc, category) => {
    acc[category.categoryKey] = category;
    return acc;
  }, {});

  const flatData = {
    ...psData,
    tools,
    categories,
    sections,
    defaultRecommended
  };

  return {
    flatData,
    structuredData
  };
};

const buildTree = psData => {
  const deepCopy = cloneDeep(psData);
  let {
    tools = [],
    categories = [],
    sections = [],
    main = [],
    defaultRecommended = [],
    isLoggedIn
  } = deepCopy;
  const root = {};
  const [mainContent = {}] = main;
  const recommendations = getRecommendationKeys(defaultRecommended);
  root.content = mainContent;
  categories = categories.map(c =>
    handleCategory(c, sections, tools, recommendations, isLoggedIn)
  );
  root.categories = categories;
  return root;
};

const handleCategory = (
  category,
  sections,
  tools,
  recommendations,
  isLoggedIn
) => {
  const newCategory = { content: category };
  const { categoryKey = '' } = category;
  const relevantSections = getSectionsForCategory(categoryKey, sections, tools);
  const toolsForCategory = getTools(categoryKey, tools);
  const featuredTools = getFeaturedTools({
    categoryKey,
    toolsForCategory,
    recommendations,
    isLoggedIn
  });

  newCategory.featured = featuredTools;
  if (relevantSections.length) {
    newCategory.sections = relevantSections;
  } else {
    newCategory.tools = toolsForCategory;
  }
  return newCategory;
};

const getRecommendationKeys = recommendations => {
  const recommendedCategories = [];
  const recommendedApps = [];

  recommendations.forEach(({ categoryKey, appKey }) => {
    recommendedCategories.push(categoryKey);
    recommendedApps.push(appKey);
  });

  return { recommendedCategories, recommendedApps };
};

const getSectionsForCategory = (key, sections, tools) => {
  sections = sections.filter(({ categoryKey = '' }) => categoryKey === key);
  sections = sections.map(s => {
    return {
      content: s,
      tools: getTools(s.sectionKey, tools)
    };
  });
  return sections;
};

const getTools = (key, tools) => {
  return tools.filter(t => {
    return t.categoryKey === key || t.sectionKey === key;
  });
};

const getFeaturedTools = ({
  categoryKey,
  toolsForCategory,
  recommendations,
  isLoggedIn
}) => {
  /*
    If an app within a pillar category is already in the `Recommended For You`
    section, choose the other apps to feature in that pillar - e.q. if `PayPal
    Checkout` is in `Recommended For You` section, `Accept Payments` pillar should
    feature the 2nd and 3rd ranking apps, such as `Invoicing` and `Subscriptions`.

    If there are no other apps in that pillar to feature, use the recommended app in the pillar
    anyway - e.q. if `Fraud Protection` is in `Recommended For You`
    section, it should still feature it in the `Risk` pillar as it is the only app).
  */

  const { recommendedCategories, recommendedApps } = recommendations;

  const shouldReturnFirst2Apps =
    toolsForCategory.length <= 2 ||
    recommendedApps.length === 0 ||
    !recommendedCategories.includes(categoryKey) ||
    !isLoggedIn;

  if (shouldReturnFirst2Apps) return toolsForCategory.slice(0, 2);

  let featuredTools = [];

  for (let i = 0; i < toolsForCategory.length; i++) {
    if (featuredTools.length === 2) break;

    let isRecommended = recommendedApps.includes(toolsForCategory[i].appKey);

    if (!isRecommended) {
      featuredTools.push(toolsForCategory[i]);
    }
  }

  /*
    When `toolsForCategory` array has 3 apps and 2 are in the `recommendedApps` array, we
    will only have one app here in the `featuredTools` array. From the requirements, we need
    to have at least 2 featured apps per pillar / category. At this point, we know that:

      - The `recommendedApps` array will contain the first 2 from `toolsForCategory` array
      - The `featuredTools` array will never contain any of the `recommendedApps` array

    Therefore, we can just push the first app from `toolsForCategory` array to `featuredTools.
  */
  if (featuredTools.length < 2) {
    featuredTools.push(toolsForCategory[0]);
  }

  return featuredTools;
};
